import React, { useState, useEffect } from 'react';
import './AddAppointment.css';

const API_BASE = 'https://parachadental.com/dental-api';

const AddAppointment = ({ isOpen, onClose, onSave, appointmentToEdit }) => {
    const [formData, setFormData] = useState({
        patient_name: '',
        patient_email: '',
        patient_phone: '',
        patient_birth_date: '',
        patient_gender: '',
        patient_address: '',
        appointment_date: '',
        appointment_time: '',
        doctor: '',
        message: ''
    });
    const [doctors, setDoctors] = useState([]);
    const [loadingDoctors, setLoadingDoctors] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Patient search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searching, setSearching] = useState(false);
    const [searchResult, setSearchResult] = useState(null);
    const [showSearchResult, setShowSearchResult] = useState(false);
    const [patientFound, setPatientFound] = useState(false);

    // Fetch doctors for dropdown
    const fetchDoctors = async () => {
        setLoadingDoctors(true);
        try {
            const response = await fetch(`${API_BASE}/fetch-doctors-simple.php`);
            const data = await response.json();
            if (data.success) {
                setDoctors(data.doctors);
            }
        } catch (error) {
            console.error('Error fetching doctors:', error);
        } finally {
            setLoadingDoctors(false);
        }
    };

    // Search patient by ID or Phone
    const searchPatient = async () => {
        if (!searchQuery.trim()) {
            setError('Please enter Patient ID or Phone Number to search');
            return;
        }

        setSearching(true);
        setError('');

        try {
            const response = await fetch(`${API_BASE}/search-patient.php?query=${encodeURIComponent(searchQuery)}`);
            const data = await response.json();

            if (data.success && data.patient) {
                setPatientFound(true);
                setSearchResult(data.patient);
                setShowSearchResult(true);
                setError('');

                // Auto-fill form with patient data
                setFormData(prev => ({
                    ...prev,
                    patient_name: data.patient.patient_name || '',
                    patient_email: data.patient.patient_email || '',
                    patient_phone: data.patient.patient_phone || '',
                    patient_birth_date: data.patient.patient_birth_date || '',
                    patient_gender: data.patient.patient_gender || '',
                    patient_address: data.patient.patient_address || ''
                }));
            } else {
                setPatientFound(false);
                setShowSearchResult(true);
                setSearchResult(null);
                // Clear form fields for new patient
                setFormData(prev => ({
                    ...prev,
                    patient_name: '',
                    patient_email: '',
                    patient_phone: '',
                    patient_birth_date: '',
                    patient_gender: '',
                    patient_address: ''
                }));
            }
        } catch (error) {
            console.error('Error searching patient:', error);
            setError('Error searching patient. Please try again.');
        } finally {
            setSearching(false);
        }
    };

    const clearSearch = () => {
        setSearchQuery('');
        setSearchResult(null);
        setShowSearchResult(false);
        setPatientFound(false);
        setError('');
    };

    useEffect(() => {
        if (isOpen) {
            fetchDoctors();
            clearSearch();
        }
    }, [isOpen]);

    useEffect(() => {
        if (appointmentToEdit) {
            setFormData({
                patient_name: appointmentToEdit.patient_name || '',
                patient_email: appointmentToEdit.patient_email || '',
                patient_phone: appointmentToEdit.patient_phone || '',
                patient_birth_date: appointmentToEdit.patient_birth_date || '',
                patient_gender: appointmentToEdit.patient_gender || '',
                patient_address: appointmentToEdit.patient_address || '',
                appointment_date: appointmentToEdit.appointment_date || '',
                appointment_time: appointmentToEdit.appointment_time || '',
                doctor: appointmentToEdit.doctor || '',
                message: appointmentToEdit.message || ''
            });
        } else {
            setFormData({
                patient_name: '',
                patient_email: '',
                patient_phone: '',
                patient_birth_date: '',
                patient_gender: '',
                patient_address: '',
                appointment_date: '',
                appointment_time: '',
                doctor: '',
                message: ''
            });
        }
        setError('');
        clearSearch();
    }, [appointmentToEdit, isOpen]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // Validate phone number format
        const phoneRegex = /^[0-9+\-\s()]{10,15}$/;
        if (!phoneRegex.test(formData.patient_phone)) {
            setError('Please enter a valid phone number');
            setLoading(false);
            return;
        }

        try {
            let response;
            if (appointmentToEdit) {
                console.log('Editing appointment with ID:', appointmentToEdit.id);
                console.log('Form data being sent:', formData);

                response = await fetch(`${API_BASE}/update-appointment.php`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        id: appointmentToEdit.id,
                        patient_name: formData.patient_name,
                        patient_email: formData.patient_email,
                        patient_phone: formData.patient_phone,
                        patient_birth_date: formData.patient_birth_date,
                        patient_gender: formData.patient_gender,
                        patient_address: formData.patient_address,
                        appointment_date: formData.appointment_date,
                        appointment_time: formData.appointment_time,
                        doctor: formData.doctor,
                        message: formData.message,
                        status: appointmentToEdit.status
                    })
                });
            } else {
                response = await fetch(`${API_BASE}/insert-appointment.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(formData)
                });
            }

            const data = await response.json();
            console.log('Server response:', data);

            if (data.success) {
                let message = `✅ APPOINTMENT ${appointmentToEdit ? 'UPDATED' : 'CONFIRMED'}!\n\n`;
                message += `Patient: ${data.appointment?.patient_name || formData.patient_name}\n`;
                message += `Doctor: ${data.appointment?.doctor || formData.doctor}\n`;
                message += `Date: ${data.appointment?.date || formData.appointment_date}\n`;
                message += `Time: ${data.appointment?.time || formData.appointment_time}\n`;

                if (appointmentToEdit && data.updated_data) {
                    message += `\n✅ Changes saved successfully!`;
                }

                alert(message);
                onSave();
                onClose();
            } else {
                setError(data.error || 'Failed to save appointment');
            }
        } catch (err) {
            console.error('Error:', err);
            setError('Cannot connect to server. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-container">
                <div className="modal-header">
                    <h2>{appointmentToEdit ? 'Edit Appointment' : 'Create New Appointment'}</h2>
                    <button className="modal-close" onClick={onClose}>&times;</button>
                </div>

                {error && <div className="modal-error">{error}</div>}

                {/* Patient Search Section - Only show for new appointments */}
                {!appointmentToEdit && (
                    <div className="patient-search-section">
                        <div className="search-header">
                            <h3>🔍 Find Existing Patient</h3>
                            <p>Search by Patient ID or Phone Number to auto-fill patient details</p>
                        </div>
                        <div className="search-input-group">
                            <input
                                type="text"
                                placeholder="Enter Patient ID or Phone Number"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="search-input"
                            />
                            <button
                                type="button"
                                onClick={searchPatient}
                                disabled={searching}
                                className="search-btn"
                            >
                                {searching ? 'Searching...' : 'Search Patient'}
                            </button>
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={clearSearch}
                                    className="clear-search-btn"
                                >
                                    ✖
                                </button>
                            )}
                        </div>

                        {showSearchResult && (
                            <div className={`search-result ${patientFound ? 'found' : 'not-found'}`}>
                                {patientFound ? (
                                    <div className="result-found">
                                        <span className="success-icon">✅</span>
                                        <span>Patient found! Details auto-filled below.</span>
                                    </div>
                                ) : (
                                    <div className="result-not-found">
                                        <span className="info-icon">ℹ️</span>
                                        <span>No patient found. Fill the form below to register a new patient.</span>
                                    </div>
                                )}
                            </div>
                        )}
                        <hr className="search-divider" />
                    </div>
                )}

                <form onSubmit={handleSubmit} className="modal-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label>Patient Name *</label>
                            <input type="text" name="patient_name" value={formData.patient_name} onChange={handleChange} required placeholder="Enter patient name" />
                        </div>
                        <div className="form-group">
                            <label>Patient Email *</label>
                            <input type="email" name="patient_email" value={formData.patient_email} onChange={handleChange} required placeholder="patient@example.com" />
                        </div>
                        <div className="form-group">
                            <label>Patient Phone *</label>
                            <input type="tel" name="patient_phone" value={formData.patient_phone} onChange={handleChange} required placeholder="0300 1234567" />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Select Doctor *</label>
                            <select name="doctor" value={formData.doctor} onChange={handleChange} required disabled={loadingDoctors}>
                                <option value="">{loadingDoctors ? 'Loading doctors...' : 'Select Doctor'}</option>
                                {doctors.map((doc) => (
                                    <option key={doc.id} value={doc.doctor_name}>{doc.doctor_name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Date of Birth</label>
                            <input type="date" name="patient_birth_date" value={formData.patient_birth_date} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label>Patient Gender</label>
                            <select name="patient_gender" value={formData.patient_gender} onChange={handleChange}>
                                <option value="">Select Gender</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group form-group-full">
                            <label>Patient Address</label>
                            <textarea name="patient_address" value={formData.patient_address} onChange={handleChange} rows="2" placeholder="Patient address..."></textarea>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Appointment Date *</label>
                            <input type="date" name="appointment_date" value={formData.appointment_date} onChange={handleChange} required />
                        </div>
                        <div className="form-group">
                            <label>Appointment Time *</label>
                            <input type="time" name="appointment_time" value={formData.appointment_time} onChange={handleChange} required />
                        </div>
                        <div className="form-group">
                            <label>Message / Notes</label>
                            <input type="text" name="message" value={formData.message} onChange={handleChange} placeholder="Any additional information..." />
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
                        <button type="submit" className="btn-save" disabled={loading}>
                            {loading ? 'Saving...' : (appointmentToEdit ? 'Update Appointment' : 'Save Appointment')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddAppointment;